import React, { FC } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { DashboardScope } from '@api/domain/types';
import type { DashboardResult } from '@api/domain/types';

import { isMachineResult, isCustomerResult, isPartResult, isNcFileResult } from '@v2/pages/dashboard/common/helpers';
import { DASHBOARD_OVERVIEW_BOTTOM_BLOCK_PAGE_SIZE, ROW_LAYOUT } from '@v2/pages/dashboard/common/constants/rows';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import type { Rank, RankBadgeVariant, RowBorderRadiusConfig } from '@v2/pages/dashboard/common/types/rows';
import DashboardOverviewBottomBlockMachineRow from './components/DashboardOverviewBottomBlockMachineRow';
import DashboardOverviewBottomBlockCustomerRow from './components/DashboardOverviewBottomBlockCustomerRow';
import DashboardOverviewBottomBlockPartRow from './components/DashboardOverviewBottomBlockPartRow';
import DashboardOverviewBottomBlockNcFileRow from './components/DashboardOverviewBottomBlockNcFileRow';
import DashboardOverviewBottomBlockRowSkeleton from './components/DashboardOverviewBottomBlockRowSkeleton';
import { useManageDashboardOverviewBottomBlockPanel } from './hooks/useManageDashboardOverviewBottomBlockPanel';

export interface DashboardOverviewBottomBlockPanelProps extends WithColor {
  variant: RankBadgeVariant;
  scope: DashboardScope;
  results: DashboardResult[] | null;
  loading: boolean;
  error: string | null;
  onShowAll?: () => void;
  onRowPress: (item: DashboardResult) => void;
}

const DashboardOverviewBottomBlockPanel: FC<DashboardOverviewBottomBlockPanelProps> = ({ C, variant, scope, results, loading, error, onShowAll, onRowPress }) => {
  const shadow = getBoxShadowStyle({ C, type: 'Narrow' });
  const { title, panelTestId, showAllTestId } = useManageDashboardOverviewBottomBlockPanel({ scope, variant });

  const renderRow = (result: DashboardResult, index: number, borderRadiusConfig: RowBorderRadiusConfig) => {
    const rank = (index + 1) as Rank;
    const onPress = () => onRowPress(result);
    if (isMachineResult(result) && scope === DashboardScope.Machine) {
      return (
        <DashboardOverviewBottomBlockMachineRow
          key={`${variant}-${rank}`}
          rank={rank}
          variant={variant}
          shortName={result.shortName}
          fullName={result.fullName}
          machineId={result.machineId}
          totalTimeTotalInSeconds={result.totalTimeTotalInSeconds}
          onPress={onPress}
          borderRadiusConfig={borderRadiusConfig}
        />
      );
    }
    if (isCustomerResult(result) && scope === DashboardScope.Customer) {
      return (
        <DashboardOverviewBottomBlockCustomerRow
          key={`${variant}-${rank}`}
          rank={rank}
          variant={variant}
          customerName={result.customerName}
          customerId={result.customerId}
          totalTimeTotalInSeconds={result.totalTimeTotalInSeconds}
          onPress={onPress}
          borderRadiusConfig={borderRadiusConfig}
        />
      );
    }
    if (isPartResult(result) && scope === DashboardScope.Part) {
      return (
        <DashboardOverviewBottomBlockPartRow
          key={`${variant}-${rank}`}
          rank={rank}
          variant={variant}
          partName={result.partName}
          description={result.description}
          partId={result.partId}
          imageHash={result.imageHash}
          imageData={result.imageData}
          totalTimeTotalInSeconds={result.totalTimeTotalInSeconds}
          onPress={onPress}
          borderRadiusConfig={borderRadiusConfig}
        />
      );
    }
    if (isNcFileResult(result) && scope === DashboardScope.NcFile) {
      return (
        <DashboardOverviewBottomBlockNcFileRow
          key={`${variant}-${rank}`}
          rank={rank}
          variant={variant}
          ncFileName={result.ncFileName}
          ncFileId={result.ncFileId}
          imageHash={result.imageHash}
          imageData={result.imageData}
          totalTimeTotalInSeconds={result.totalTimeTotalInSeconds}
          onPress={onPress}
          borderRadiusConfig={borderRadiusConfig}
        />
      );
    }
    return null;
  };

  return (
    <View style={{ flex: 1 }} testID={panelTestId}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: scale(10),
        }}
      >
        <Text style={[font['Title 3'], { color: C('Label/Light/Primary') }]}>{title}</Text>
        {onShowAll && (
          <TouchableElement testID={showAllTestId} onPress={onShowAll}>
            {({ boxStyle }) => (
              <View style={boxStyle}>
                <Text style={[font['Subhead 2'], { color: C('System/Light/Blue') }]}>{i18n.t('Show all')}</Text>
              </View>
            )}
          </TouchableElement>
        )}
      </View>

      <View
        style={[
          shadow,
          {
            backgroundColor: C('Background/Light/System'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
          },
        ]}
      >
        {loading ? (
          Array.from({ length: DASHBOARD_OVERVIEW_BOTTOM_BLOCK_PAGE_SIZE }).map((_, i) => (
            <View key={i}>
              {i > 0 && <View style={{ height: ROW_LAYOUT.divider.height, backgroundColor: C(ROW_LAYOUT.divider.color) }} />}
              <DashboardOverviewBottomBlockRowSkeleton
                scope={scope}
                borderRadiusConfig={{ top: i === 0, bottom: i === DASHBOARD_OVERVIEW_BOTTOM_BLOCK_PAGE_SIZE - 1 }}
              />
            </View>
          ))
        ) : error ? (
          <View style={{ paddingVertical: scale(24), alignItems: 'center' }}>
            <Text style={[font['Subhead 2'], { color: C('System/Light/Red') }]}>{i18n.t('Error')}</Text>
          </View>
        ) : !results || results.length === 0 ? (
          <View testID={TEST_IDS.dashboardOverviewBottomBlock.noData(variant)} style={{ paddingVertical: scale(24), alignItems: 'center' }}>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('No data')}</Text>
          </View>
        ) : (
          (() => {
            const visibleResults = results.slice(0, DASHBOARD_OVERVIEW_BOTTOM_BLOCK_PAGE_SIZE);
            const lastIndex = visibleResults.length - 1;
            return visibleResults.map((result, index) => (
              <View key={`${variant}-${index}`}>
                {index > 0 && <View style={{ height: ROW_LAYOUT.divider.height, backgroundColor: C(ROW_LAYOUT.divider.color) }} />}
                {renderRow(result, index, { top: index === 0, bottom: index === lastIndex })}
              </View>
            ));
          })()
        )}
      </View>
    </View>
  );
};

export default ColorWrapper(DashboardOverviewBottomBlockPanel);
